import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/common/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Eye, ShieldCheck, Landmark } from "lucide-react";

const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 focus-visible:ring-cyan-500";

export default function BankingSettings() {
  const qc = useQueryClient();
  const [f, setF] = useState(null);
  const [acct, setAcct] = useState("");
  const [payid, setPayid] = useState("");
  const [methods, setMethods] = useState([]);
  const { data } = useQuery({ queryKey: ["banking-settings"], queryFn: async () => (await api.get("/banking/settings")).data });
  useEffect(() => { if (data && !f) { setF(data); setMethods(data.methods || []); } }, [data, f]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        account_name: f.account_name, bank_name: f.bank_name, bsb: f.bsb, payid_type: f.payid_type,
        default_currency: f.default_currency, default_payment_terms: f.default_payment_terms,
        custom_terms_days: Number(f.custom_terms_days) || 0, default_instructions: f.default_instructions,
      };
      if (acct.trim()) payload.account_number = acct.trim();
      if (payid.trim()) payload.payid_value = payid.trim();
      return (await api.put("/banking/settings", payload)).data;
    },
    onSuccess: (d) => { setF(d); setAcct(""); setPayid(""); qc.invalidateQueries({ queryKey: ["banking-settings"] }); toast.success("Banking settings saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const saveMethods = useMutation({
    mutationFn: async () => (await api.put("/banking/methods", { methods })).data,
    onSuccess: (m) => { setMethods(m); toast.success("Payment methods saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const reveal = useMutation({
    mutationFn: async () => (await api.post("/banking/settings/reveal")).data,
    onSuccess: (d) => { setAcct(d.account_number || ""); setPayid(d.payid_value || ""); toast.success("Revealed — remember to keep these secure"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (!f) return null;
  const setMethod = (i, patch) => setMethods(methods.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));

  return (
    <div data-testid="banking-settings-page">
      <PageHeader title="Banking & Payments" subtitle="Business bank account, PayID and accepted payment methods. Sensitive values are encrypted and masked." />

      <div className="mb-3 flex items-center gap-2 rounded-lg border border-cyan-500/20 bg-cyan-500/5 px-3 py-2 text-xs text-cyan-200">
        <ShieldCheck className="h-4 w-4" /> Restricted to super administrators. Every change is recorded in the audit log; full account numbers are never logged.
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
          <div className="mb-3 flex items-center justify-between">
            <SectionTitle>Business bank account</SectionTitle>
            <Button size="sm" variant="ghost" className="text-cyan-300 hover:bg-cyan-500/10" onClick={() => reveal.mutate()} data-testid="reveal-btn"><Eye className="mr-1 h-3.5 w-3.5" /> Reveal</Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs text-slate-400">Account name</Label><Input value={f.account_name || ""} onChange={(e) => setF({ ...f, account_name: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="account-name-input" /></div>
            <div><Label className="text-xs text-slate-400">Bank</Label><Input value={f.bank_name || ""} onChange={(e) => setF({ ...f, bank_name: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="bank-name-input" /></div>
            <div><Label className="text-xs text-slate-400">BSB</Label><Input value={f.bsb || ""} onChange={(e) => setF({ ...f, bsb: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="bsb-input" /></div>
            <div className="col-span-2"><Label className="text-xs text-slate-400">Account number {f.has_account_number && !acct ? `(current: ${f.account_number_masked})` : ""}</Label><Input value={acct} onChange={(e) => setAcct(e.target.value)} placeholder={f.has_account_number ? f.account_number_masked : "Enter account number"} className={`mt-1 ${inputCls}`} data-testid="account-number-input" /></div>
            <div><Label className="text-xs text-slate-400">PayID type</Label>
              <Select value={f.payid_type || ""} onValueChange={(v) => setF({ ...f, payid_type: v })}>
                <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="payid-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
                  {["ABN", "Email", "Phone", "Org ID"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-slate-400">PayID value {f.has_payid && !payid ? `(current: ${f.payid_masked})` : ""}</Label><Input value={payid} onChange={(e) => setPayid(e.target.value)} placeholder={f.has_payid ? f.payid_masked : "e.g. ABN or email"} className={`mt-1 ${inputCls}`} data-testid="payid-value-input" /></div>
          </div>
        </div>

        <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
          <SectionTitle className="mb-3">Defaults</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-400">Default currency</Label><Input value={f.default_currency || "AUD"} onChange={(e) => setF({ ...f, default_currency: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="currency-input" /></div>
            <div><Label className="text-xs text-slate-400">Default payment terms</Label>
              <Select value={f.default_payment_terms || ""} onValueChange={(v) => setF({ ...f, default_payment_terms: v })}>
                <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="terms-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
                  {(f.payment_terms_options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {f.default_payment_terms === "Custom" && (
              <div><Label className="text-xs text-slate-400">Custom terms (days)</Label><Input type="number" value={f.custom_terms_days || 0} onChange={(e) => setF({ ...f, custom_terms_days: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="custom-days-input" /></div>
            )}
            <div className="col-span-2"><Label className="text-xs text-slate-400">Default invoice payment instructions</Label><Textarea rows={3} value={f.default_instructions || ""} onChange={(e) => setF({ ...f, default_instructions: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="instructions-input" /></div>
          </div>
          <Button onClick={() => save.mutate()} disabled={save.isPending} className="mt-4 bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="save-banking-btn"><Save className="mr-1.5 h-4 w-4" /> Save banking details</Button>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4" data-testid="methods-section">
        <SectionTitle className="mb-1 flex items-center gap-2"><Landmark className="h-4 w-4 text-cyan-400" /> Accepted payment methods</SectionTitle>
        <p className="mb-3 text-xs text-slate-500">EFT and PayID are the fee-free defaults. Others are structural and stay disabled until a provider is configured.</p>
        <div className="space-y-2">
          {methods.map((m, i) => (
            <div key={m.key} className="rounded-lg border border-[#1E2D4A] bg-[#0B1120] p-3" data-testid={`method-${m.key}`}>
              <div className="flex flex-wrap items-center gap-3">
                <Button size="sm" variant={m.enabled ? "default" : "outline"} className={m.enabled ? "h-7 bg-emerald-600 text-xs hover:bg-emerald-500" : "h-7 border-[#2D3F62] bg-transparent text-xs text-slate-400"} onClick={() => setMethod(i, { enabled: !m.enabled })} data-testid={`method-toggle-${m.key}`}>{m.enabled ? "Enabled" : "Disabled"}</Button>
                <Input value={m.display_name || ""} onChange={(e) => setMethod(i, { display_name: e.target.value })} className={`h-8 max-w-[220px] text-sm ${inputCls}`} data-testid={`method-name-${m.key}`} />
                <span className="text-[11px] uppercase tracking-wide text-slate-600">{m.provider}</span>
              </div>
              <Textarea rows={2} value={m.instructions || ""} onChange={(e) => setMethod(i, { instructions: e.target.value })} placeholder="Customer-visible instructions" className={`mt-2 text-sm ${inputCls}`} data-testid={`method-instructions-${m.key}`} />
            </div>
          ))}
        </div>
        <Button onClick={() => saveMethods.mutate()} disabled={saveMethods.isPending} className="mt-4 bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="save-methods-btn"><Save className="mr-1.5 h-4 w-4" /> Save methods</Button>
      </div>
    </div>
  );
}
